import { useApp } from '../../context/AppContext';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Info, PauseCircle } from 'lucide-react';

export const ContributionPause = () => {
  const { inputs, updateInput, t, language } = useApp();
  
  const handlePauseToggle = (checked) => {
    updateInput('pauseEnabled', checked);
    if (!checked) {
      updateInput('pauseStartYear', 0);
      updateInput('pauseEndYear', 0);
    } else {
      // Set default pause values
      const startYear = Math.max(1, Math.floor(inputs.years / 2));
      updateInput('pauseStartYear', startYear);
      updateInput('pauseEndYear', Math.min(inputs.years, startYear + 5));
    }
  };
  
  return (
    <div className="space-y-4" data-testid="contribution-pause">
      <div className="flex items-center space-x-3">
        <Checkbox
          id="pauseEnabled"
          checked={inputs.pauseEnabled}
          onCheckedChange={handlePauseToggle}
          data-testid="pause-enabled-checkbox"
        />
        <Label 
          htmlFor="pauseEnabled" 
          className="text-sm font-semibold cursor-pointer flex items-center gap-2"
        >
          <PauseCircle className="h-4 w-4 text-orange-500" />
          {t('contributionPause')}
        </Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3.5 w-3.5 text-foreground/50 cursor-help" />
            </TooltipTrigger>
            <TooltipContent 
              side="top" 
              className="max-w-xs bg-popover text-popover-foreground border shadow-lg"
            >
              <p className="text-sm">{t('tooltipPause')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      {inputs.pauseEnabled && (
        <div className="p-5 rounded-2xl bg-orange-100 dark:bg-orange-900/40 border-2 border-orange-200 dark:border-orange-800 space-y-4">
          <p className="text-sm text-orange-700 dark:text-orange-300">
            {t('pauseDescription')}
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.12em] text-orange-700 dark:text-orange-300 font-semibold">
                {t('pauseStartYear')}
              </Label>
              <Input
                type="number"
                value={inputs.pauseStartYear}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 0;
                  updateInput('pauseStartYear', Math.min(val, inputs.years - 1));
                }}
                className="w-full font-semibold h-11 text-base bg-white dark:bg-orange-950"
                min="1"
                max={inputs.years - 1}
                data-testid="pause-start-year-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.12em] text-orange-700 dark:text-orange-300 font-semibold">
                {t('pauseEndYear')}
              </Label>
              <Input
                type="number"
                value={inputs.pauseEndYear}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 0;
                  updateInput('pauseEndYear', Math.max(inputs.pauseStartYear, Math.min(val, inputs.years)));
                }}
                className="w-full font-semibold h-11 text-base bg-white dark:bg-orange-950"
                min={inputs.pauseStartYear}
                max={inputs.years}
                data-testid="pause-end-year-input"
              />
            </div>
          </div>
          
          {inputs.pauseStartYear > 0 && inputs.pauseEndYear > 0 && (
            <div className="text-sm font-medium text-orange-800 dark:text-orange-200 bg-orange-200 dark:bg-orange-800 rounded-lg px-3 py-2">
              {language === 'ro' 
                ? `Pauză de ${inputs.pauseEndYear - inputs.pauseStartYear + 1} ani (din anul ${inputs.pauseStartYear} până în anul ${inputs.pauseEndYear})`
                : `${inputs.pauseEndYear - inputs.pauseStartYear + 1} year pause (from year ${inputs.pauseStartYear} to year ${inputs.pauseEndYear})`
              }
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContributionPause;
